import { formatCount } from './format';

// Which controls the filter bar offers, and what the ones that vary are called.
//
// Pure, with no DOM in it, because the gating is the part that breaks silently. Every control in
// the bar is conditional on the CURRENT response, and a control that should have disappeared but
// did not renders a select that can only narrow to nothing — nothing throws, nothing fails, the row
// count is just wrong. The rules lived as four inline conditions in TableControls' JSX plus a
// self-gate inside IndexFilter, so the only way to ask "does this bar offer a sector filter for
// this response" was to render it and look.
//
// The shared rule behind all of them: a control that cannot change what is on screen is worse than
// no control. A sector select over rows with no sector, a date filter over a single occasion, an
// average toggle where every company has one row — each would be pressed once, to learn nothing.
//
// Keeping this a model rather than a set of booleans in the component is also what a "consolidate
// into one filter bar" redesign needs: a drawer or an overflow menu has to make the same decisions
// this bar makes, and behind a closed popover a filter that should have unmounted is
// indistinguishable from one that did.
export const filterBarModel = ({
  missingCount = 0,
  matchCount = 0,
  totalCount = 0,
  sectors = [],
  hasInputs = false,
  canAggregate = false,
  canChangeAggregate = false,
  isFiltering = false,
}) => ({
  // The predicate is the same either way — the rows with no score — but only a feed that composes
  // members may call that a missing MEMBER. On a single-input feed there are none to be missing, and
  // the toggle used to claim otherwise on the strength of a null score alone. "Missing scores" is
  // the neutral truth, so it also covers the slim read, where the markers were dropped and the
  // feed's kind is not knowable from the response.
  showMissing: missingCount > 0,
  missingLabel: `${hasInputs ? 'Missing inputs only' : 'Missing scores only'} (${formatCount(missingCount)})`,

  showSector: sectors.length > 0,

  // NOT here: whether the index filter and the knowable-window filter appear. Both components own
  // that rule themselves — the window filter because it has to when reparented, the index filter
  // because its vocabulary is its own — and a second copy here would reinstate exactly the
  // two-places-decide-one-thing split this module exists to end.

  // Two conditions, not one: a caller that can aggregate but hands over no handler gets no control
  // rather than a dead one.
  showAggregate: canAggregate && canChangeAggregate,

  // One clear for the bar, offered only while something is actually narrowed.
  showClearAll: isFiltering,

  // Stated only while something is actually narrowed — on an unfiltered table it would be a count
  // of everything against everything.
  showMatchCount: matchCount !== totalCount,
  matchLabel: `${formatCount(matchCount)} of ${formatCount(totalCount)} rows match`,
});
